// src/services/warehouseService.ts
import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  getDocs,
  getDoc,
  orderBy,
  query,
  where,
  serverTimestamp,
  limit,
  startAfter,
  getCountFromServer,
  QueryDocumentSnapshot,
  DocumentData,
  Query,
} from 'firebase/firestore';
import { db } from '../../../firebase/config';
import {
  Warehouse,
  WarehouseFormData,
  WarehouseAddress,
  WarehouseContact,
  WarehouseCapacity,
  WarehouseOperatingHours,
  WarehouseDashboardStats,
  WarehouseWithInventory,
  WarehouseInventory,
} from '../../../enums/product';
import { WarehouseInventoryService } from '../product/WarehouseInventoryService';

const WAREHOUSES_COLLECTION = 'warehouses';

export interface PaginatedWarehousesResult {
  warehouses: Warehouse[];
  totalCount: number;
  // Kept for interface compatibility — always null now (seek-based pagination
  // doesn't need callers to pass cursors back in).
  lastVisible: QueryDocumentSnapshot<DocumentData> | null;
  firstVisible: QueryDocumentSnapshot<DocumentData> | null;
}

interface GroupedProduct {
  productId: string;
  hasVariants: boolean;
  totalQuantity: number;
  isOutOfStock: boolean;
  isLowStock: boolean;
  variants: WarehouseInventory[];
}

export class WarehouseService {

  /* ===================== GET WAREHOUSES PAGINATED =====================
   *
   * FIX: Replaced cursor-based pagination (startAfter / endBefore /
   * limitToLast) with a two-step cursor-seek so any page can be fetched
   * in any order (page 1 → page 5 → page 2 all work correctly).
   *
   * HOW IT WORKS
   * ─────────────
   * Firestore's client SDK has no offset(). The classic startAfter cursor
   * only works sequentially. To jump to page N we:
   *
   *   Step 1 — fetch only the first (page-1)*pageSize doc IDs (lightweight
   *             seek). The last doc from this set becomes our startAfter cursor.
   *   Step 2 — fetch the real pageSize docs starting after that cursor.
   *
   * For page 1 the seek is skipped entirely (skip count = 0).
   * ========================================================= */
  static async getWarehousesPaginated(
    pageSize: number = 10,
    page: number = 1,
    searchQuery?: string,
    statusFilter?: boolean
  ): Promise<PaginatedWarehousesResult> {
    try {
      const warehouses: Warehouse[] = [];
      const collectionRef = collection(db, WAREHOUSES_COLLECTION);

      // ── Build filter constraints (where clauses only, no ordering/paging) ──
      const filterConstraints: any[] = [];
      if (statusFilter !== undefined) {
        filterConstraints.push(where('isActive', '==', statusFilter));
      }

      const orderConstraint = orderBy('createdAt', 'desc');

      // ── Total count (0 doc reads) ──────────────────────────────────────────
      const countSnapshot = await getCountFromServer(
        query(collectionRef, ...filterConstraints)
      );
      const totalCount = countSnapshot.data().count;

      // ── Step 1: cursor seek ────────────────────────────────────────────────
      let cursorDoc: QueryDocumentSnapshot<DocumentData> | null = null;
      const skipCount = (page - 1) * pageSize;

      if (skipCount > 0) {
        const seekSnapshot = await getDocs(
          query(collectionRef, ...filterConstraints, orderConstraint, limit(skipCount))
        );
        if (!seekSnapshot.empty) {
          cursorDoc = seekSnapshot.docs[seekSnapshot.docs.length - 1];
        }
      }

      // ── Step 2: fetch the real page ────────────────────────────────────────
      const pageQuery: Query<DocumentData> = cursorDoc
        ? query(collectionRef, ...filterConstraints, orderConstraint, startAfter(cursorDoc), limit(pageSize))
        : query(collectionRef, ...filterConstraints, orderConstraint, limit(pageSize));

      const snapshot = await getDocs(pageQuery);

      // ── Map + client-side search filter ───────────────────────────────────
      snapshot.docs.forEach(docSnap => {
        const data = docSnap.data();
        const warehouse: Warehouse = {
          id: docSnap.id,
          name: data.name || '',
          code: data.code || '',
          description: data.description || '',
          address: data.address as WarehouseAddress || { street: '', city: '', state: '', country: '', zipCode: '' },
          contact: data.contact as WarehouseContact || { phone: '', email: '', manager: '' },
          capacity: data.capacity as WarehouseCapacity || { total: 0, used: 0, available: 0 },
          isActive: data.isActive !== false,
          displayInWebsite: data.displayInWebsite === true,
          operatingHours: data.operatingHours as WarehouseOperatingHours | undefined,
          createdAt: data.createdAt?.toDate(),
          updatedAt: data.updatedAt?.toDate(),
          createdBy: data.createdBy,
          createdByName: data.createdByName,
          updatedBy: data.updatedBy,
          updatedByName: data.updatedByName,
        };

        if (searchQuery && searchQuery.trim() !== '') {
          const s = searchQuery.toLowerCase();
          const match =
            warehouse.name.toLowerCase().includes(s) ||
            warehouse.code.toLowerCase().includes(s) ||
            warehouse.description.toLowerCase().includes(s) ||
            warehouse.address.street.toLowerCase().includes(s) ||
            warehouse.address.city.toLowerCase().includes(s) ||
            warehouse.address.state.toLowerCase().includes(s) ||
            warehouse.address.country.toLowerCase().includes(s) ||
            warehouse.address.zipCode.toLowerCase().includes(s) ||
            warehouse.contact.manager.toLowerCase().includes(s) ||
            warehouse.contact.phone.toLowerCase().includes(s) ||
            warehouse.contact.email.toLowerCase().includes(s);
          if (match) warehouses.push(warehouse);
        } else {
          warehouses.push(warehouse);
        }
      });

      return {
        warehouses,
        totalCount,
        lastVisible: null,   // no longer used by callers
        firstVisible: null,  // no longer used by callers
      };
    } catch (error) {
      console.error('Error fetching paginated warehouses:', error);
      throw error;
    }
  }

  static async createWarehouse(formData: WarehouseFormData, userId: string, userName: string): Promise<string> {
    try {
      const existingWarehouse = await this.getWarehouseByCode(formData.code);
      if (existingWarehouse) throw new Error('Warehouse code already exists');

      const totalCapacity = parseFloat(formData.capacity.total) || 0;
      const usedCapacity = parseFloat(formData.capacity.used) || 0;

      const capacity: WarehouseCapacity = {
        total: totalCapacity,
        used: usedCapacity,
        available: totalCapacity - usedCapacity,
      };

      const warehouseData = {
        name: formData.name,
        code: formData.code.toUpperCase(),
        description: formData.description,
        address: formData.address,
        contact: formData.contact,
        capacity,
        isActive: formData.isActive,
        displayInWebsite: formData.displayInWebsite ?? false,
        operatingHours: formData.operatingHours,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        createdBy: userId,
        createdByName: userName,
      };

      const docRef = await addDoc(collection(db, WAREHOUSES_COLLECTION), warehouseData);
      return docRef.id;
    } catch (error) {
      console.error('Error creating warehouse:', error);
      throw error;
    }
  }

  private static groupInventoryProducts(inventory: WarehouseInventory[]): GroupedProduct[] {
    const grouped = new Map<string, GroupedProduct>();

    inventory.forEach(item => {
      if (!grouped.has(item.productId)) {
        grouped.set(item.productId, {
          productId: item.productId,
          hasVariants: !!item.variantId,
          totalQuantity: 0,
          isOutOfStock: false,
          isLowStock: false,
          variants: [],
        });
      }

      const group = grouped.get(item.productId)!;
      if (item.variantId) {
        group.variants.push(item);
        group.totalQuantity += item.quantity;
      } else {
        group.totalQuantity = item.quantity;
        group.isOutOfStock = item.isOutOfStock;
        group.isLowStock = item.isLowStock;
      }
    });

    grouped.forEach(group => {
      if (group.hasVariants && group.variants.length > 0) {
        group.isOutOfStock = group.variants.every(v => v.isOutOfStock);
        group.isLowStock = !group.isOutOfStock && group.variants.some(v => v.isLowStock);
      }
    });

    return Array.from(grouped.values());
  }

  static async getWarehouseWithInventory(warehouseId: string): Promise<WarehouseWithInventory | null> {
    try {
      const warehouse = await this.getWarehouse(warehouseId);
      if (!warehouse) return null;

      const inventory = await WarehouseInventoryService.getWarehouseInventory(warehouseId);
      const groupedProducts = this.groupInventoryProducts(inventory);
      const totalStock = inventory.reduce((sum, item) => sum + item.quantity, 0);
      const lowStockCount = groupedProducts.filter(p => p.isLowStock && !p.isOutOfStock).length;
      const outOfStockCount = groupedProducts.filter(p => p.isOutOfStock).length;

      return {
        ...warehouse,
        inventory,
        stats: {
          productCount: groupedProducts.length,
          variantCount: inventory.filter(i => i.variantId).length,
          totalStock,
          lowStockCount,
          outOfStockCount,
        },
      };
    } catch (error) {
      console.error('Error fetching warehouse with inventory:', error);
      throw error;
    }
  }

  static async getActiveWarehousesWithCapacity(): Promise<Warehouse[]> {
    try {
      const q = query(
        collection(db, WAREHOUSES_COLLECTION),
        where('isActive', '==', true),
        orderBy('name', 'asc')
      );
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => this.mapDocToWarehouse(doc));
    } catch (error) {
      console.error('Error fetching active warehouses:', error);
      throw error;
    }
  }

  static async getWarehouseUtilizationStats(warehouseId: string): Promise<{
    utilizationRate: number;
    capacityUsed: number;
    capacityAvailable: number;
    capacityTotal: number;
  }> {
    try {
      const warehouse = await this.getWarehouse(warehouseId);
      if (!warehouse) throw new Error('Warehouse not found');

      const utilizationRate = Math.round((warehouse.capacity.used / warehouse.capacity.total) * 100);
      return {
        utilizationRate,
        capacityUsed: warehouse.capacity.used,
        capacityAvailable: warehouse.capacity.available,
        capacityTotal: warehouse.capacity.total,
      };
    } catch (error) {
      console.error('Error getting warehouse utilization stats:', error);
      throw error;
    }
  }

  static async getDashboardStats(): Promise<WarehouseDashboardStats> {
    try {
      const warehouses = await this.getWarehouses();
      const total = warehouses.length;
      const active = warehouses.filter(w => w.isActive).length;
      const inactive = total - active;
      const totalCapacity = warehouses.reduce((sum, w) => sum + w.capacity.total, 0);
      const usedCapacity = warehouses.reduce((sum, w) => sum + w.capacity.used, 0);
      const availableCapacity = totalCapacity - usedCapacity;
      const utilizationRate = totalCapacity > 0 ? Math.round((usedCapacity / totalCapacity) * 100) : 0;

      return { total, active, inactive, totalCapacity, usedCapacity, availableCapacity, utilizationRate };
    } catch (error) {
      console.error('Error getting dashboard stats:', error);
      throw error;
    }
  }

  static async updateWarehouseCapacity(
    warehouseId: string,
    capacity: { total?: number; used?: number; available?: number }
  ): Promise<void> {
    try {
      const warehouseRef = doc(db, WAREHOUSES_COLLECTION, warehouseId);
      const updateData: any = { updatedAt: serverTimestamp() };

      if (capacity.total !== undefined) updateData['capacity.total'] = capacity.total;
      if (capacity.used !== undefined) updateData['capacity.used'] = capacity.used;
      if (capacity.available !== undefined) updateData['capacity.available'] = capacity.available;

      await updateDoc(warehouseRef, updateData);
    } catch (error) {
      console.error('Error updating warehouse capacity:', error);
      throw error;
    }
  }

  static async getWarehousesWithLowCapacity(threshold: number = 20): Promise<Warehouse[]> {
    try {
      const warehouses = await this.getActiveWarehouses();
      return warehouses.filter(warehouse => {
        const availablePercentage = (warehouse.capacity.available / warehouse.capacity.total) * 100;
        return availablePercentage < threshold;
      });
    } catch (error) {
      console.error('Error getting warehouses with low capacity:', error);
      throw error;
    }
  }

  static async updateWarehouse(warehouseId: string, formData: WarehouseFormData, userId: string, userName: string): Promise<void> {
    try {
      const existingWarehouse = await this.getWarehouse(warehouseId);
      if (!existingWarehouse) throw new Error('Warehouse not found');

      const warehouseWithSameCode = await this.getWarehouseByCode(formData.code);
      if (warehouseWithSameCode && warehouseWithSameCode.id !== warehouseId) {
        throw new Error('Warehouse code already exists');
      }

      const totalCapacity = parseFloat(formData.capacity.total) || 0;
      const usedCapacity = parseFloat(formData.capacity.used) || 0;
      const capacity: WarehouseCapacity = {
        total: totalCapacity,
        used: usedCapacity,
        available: totalCapacity - usedCapacity,
      };

      const warehouseRef = doc(db, WAREHOUSES_COLLECTION, warehouseId);
      await updateDoc(warehouseRef, {
        name: formData.name,
        code: formData.code.toUpperCase(),
        description: formData.description,
        address: formData.address,
        contact: formData.contact,
        capacity,
        isActive: formData.isActive,
        displayInWebsite: formData.displayInWebsite ?? false,
        operatingHours: formData.operatingHours,
        updatedAt: serverTimestamp(),
        updatedBy: userId,
        updatedByName: userName,
      });
    } catch (error) {
      console.error('Error updating warehouse:', error);
      throw error;
    }
  }

  static async deleteWarehouse(warehouseId: string): Promise<void> {
    try {
      const hasInventory = await this.checkWarehouseInventory(warehouseId);
      if (hasInventory) {
        throw new Error('Cannot delete warehouse with existing inventory. Please transfer or remove inventory first.');
      }
      const warehouseRef = doc(db, WAREHOUSES_COLLECTION, warehouseId);
      await deleteDoc(warehouseRef);
    } catch (error) {
      console.error('Error deleting warehouse:', error);
      throw error;
    }
  }

  /**
   * @deprecated Use getWarehousesPaginated instead
   */
  static async getWarehouses(): Promise<Warehouse[]> {
    try {
      const q = query(collection(db, WAREHOUSES_COLLECTION), orderBy('createdAt', 'desc'));
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => this.mapDocToWarehouse(doc));
    } catch (error) {
      console.error('Error fetching warehouses:', error);
      throw error;
    }
  }

  static async getActiveWarehouses(): Promise<Warehouse[]> {
    try {
      const q = query(
        collection(db, WAREHOUSES_COLLECTION),
        where('isActive', '==', true),
        orderBy('createdAt', 'desc')
      );
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => this.mapDocToWarehouse(doc));
    } catch (error) {
      console.error('Error fetching active warehouses:', error);
      throw error;
    }
  }

  static async getWarehouse(warehouseId: string): Promise<Warehouse | null> {
    try {
      const warehouseRef = doc(db, WAREHOUSES_COLLECTION, warehouseId);
      const warehouseSnap = await getDoc(warehouseRef);
      if (warehouseSnap.exists()) return this.mapDocToWarehouse(warehouseSnap);
      return null;
    } catch (error) {
      console.error('Error fetching warehouse:', error);
      throw error;
    }
  }

  static async getWarehouseByCode(code: string): Promise<Warehouse | null> {
    try {
      const q = query(
        collection(db, WAREHOUSES_COLLECTION),
        where('code', '==', code.toUpperCase())
      );
      const querySnapshot = await getDocs(q);
      if (querySnapshot.empty) return null;
      return this.mapDocToWarehouse(querySnapshot.docs[0]);
    } catch (error) {
      console.error('Error fetching warehouse by code:', error);
      throw error;
    }
  }

  /* ===================== GET WAREHOUSE STATS =====================
   * Uses getCountFromServer() for total/active/inactive counts (no doc reads).
   * Capacity aggregates require document data — fetched in the same
   * Promise.all so the whole stats call is one round trip.
   * ============================================================== */
  static async getWarehouseStats(): Promise<WarehouseDashboardStats> {
    try {
      const col = collection(db, WAREHOUSES_COLLECTION);

      const [totalSnap, activeSnap, docsSnap] = await Promise.all([
        getCountFromServer(query(col)),
        getCountFromServer(query(col, where('isActive', '==', true))),
        getDocs(query(col)),
      ]);

      const total = totalSnap.data().count;
      const active = activeSnap.data().count;
      const inactive = total - active;

      let totalCapacity = 0;
      let usedCapacity = 0;
      docsSnap.forEach(d => {
        const cap = d.data().capacity;
        totalCapacity += cap?.total || 0;
        usedCapacity += cap?.used || 0;
      });

      const availableCapacity = totalCapacity - usedCapacity;
      const utilizationRate = totalCapacity > 0
        ? Math.round((usedCapacity / totalCapacity) * 100)
        : 0;

      return { total, active, inactive, totalCapacity, usedCapacity, availableCapacity, utilizationRate };
    } catch (error) {
      console.error('Error getting warehouse stats:', error);
      throw error;
    }
  }

  static async searchWarehouses(searchTerm: string): Promise<Warehouse[]> {
    try {
      const warehouses = await this.getWarehouses();
      const lower = searchTerm.toLowerCase();
      return warehouses.filter(w =>
        w.name.toLowerCase().includes(lower) ||
        w.code.toLowerCase().includes(lower) ||
        w.description.toLowerCase().includes(lower) ||
        w.contact.manager.toLowerCase().includes(lower) ||
        w.address.city.toLowerCase().includes(lower) ||
        w.address.state.toLowerCase().includes(lower) ||
        w.address.country.toLowerCase().includes(lower)
      );
    } catch (error) {
      console.error('Error searching warehouses:', error);
      throw error;
    }
  }

  // private static async checkWarehouseInventory(warehouseId: string): Promise<boolean> {
  //   const { ProductService } = await import('../product/productService');
  //   try {
  //     const productsInWarehouse = await ProductService.getProductsByWarehouse(warehouseId);
  //     return productsInWarehouse.length > 0;
  //   } catch (error) {
  //     console.error('Error checking warehouse inventory:', error);
  //     return false;
  //   }
  // }

  private static async checkWarehouseInventory(warehouseId: string): Promise<boolean> {
  try {
    const warehouseRef = doc(db, WAREHOUSES_COLLECTION, warehouseId);
    const warehouseSnap = await getDoc(warehouseRef);
    if (!warehouseSnap.exists()) return false;
    const data = warehouseSnap.data();
    const used = data?.capacity?.used ?? 0;
    const totalStock = data?.stats?.totalStock ?? 0;
    return used > 0 || totalStock > 0;
  } catch (error) {
    console.error('Error checking warehouse inventory:', error);
    return false;
  }
}

  static async getWarehouseUtilization(warehouseId: string): Promise<{
    totalProducts: number;
    totalStock: number;
    simpleProducts: number;
    variantProducts: number;
  }> {
    try {
      const { ProductService } = await import('../product/productService');
      const products = await ProductService.getProductsByWarehouse(warehouseId);
      const simpleProducts = products.filter(p => !p.hasVariants);
      const variantProducts = products.filter(p => p.hasVariants);
      const totalStock = products.reduce((sum, p) => sum + p.stockQuantity, 0);
      return {
        totalProducts: products.length,
        totalStock,
        simpleProducts: simpleProducts.length,
        variantProducts: variantProducts.length,
      };
    } catch (error) {
      console.error('Error getting warehouse utilization:', error);
      throw error;
    }
  }

  private static mapDocToWarehouse(doc: QueryDocumentSnapshot<DocumentData>): Warehouse {
    const data = doc.data();
    return {
      id: doc.id,
      name: data.name || '',
      code: data.code || '',
      description: data.description || '',
      address: data.address as WarehouseAddress || { street: '', city: '', state: '', country: '', zipCode: '' },
      contact: data.contact as WarehouseContact || { phone: '', email: '', manager: '' },
      capacity: data.capacity as WarehouseCapacity || { total: 0, used: 0, available: 0 },
      isActive: data.isActive !== false,
      displayInWebsite: data.displayInWebsite === true,
      operatingHours: data.operatingHours as WarehouseOperatingHours | undefined,
      createdAt: data.createdAt?.toDate(),
      updatedAt: data.updatedAt?.toDate(),
      createdBy: data.createdBy,
      createdByName: data.createdByName,
      updatedBy: data.updatedBy,
      updatedByName: data.updatedByName,
    };
  }
}
